import { c as _c } from "react/compiler-runtime";
import { useDay } from '@datepicker-react/hooks';
import clsx from 'clsx';
import { isSameDay } from 'date-fns';
import { useContext } from 'react';
import { formatDashedYYYYMMDD, fullDate } from 'utils';
import DatepickerContext from './DatepickerContext';
function Day(t0) {
  const $ = _c(51);
  const {
    day,
    date
  } = t0;
  const {
    startDate,
    endDate,
    range,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
    futurePricingAvailability
  } = useContext(DatepickerContext);
  let t1;
  if ($[0] !== date || $[1] !== focusedDate || $[2] !== isDateBlocked || $[3] !== isDateFocused || $[4] !== isDateHovered || $[5] !== isDateSelected || $[6] !== isFirstOrLastSelectedDate || $[7] !== onDateFocus || $[8] !== onDateHover || $[9] !== onDateSelect) {
    t1 = {
      date,
      focusedDate,
      isDateFocused,
      isDateSelected,
      isDateHovered,
      isDateBlocked,
      isFirstOrLastSelectedDate,
      onDateFocus,
      onDateSelect,
      onDateHover
    };
    $[0] = date;
    $[1] = focusedDate;
    $[2] = isDateBlocked;
    $[3] = isDateFocused;
    $[4] = isDateHovered;
    $[5] = isDateSelected;
    $[6] = isFirstOrLastSelectedDate;
    $[7] = onDateFocus;
    $[8] = onDateHover;
    $[9] = onDateSelect;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const {
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    disabledDate,
    onClick,
    onKeyDown,
    onMouseEnter,
    tabIndex
  } = useDay(t1);
  if (!day) {
    let t2;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="w-8 h-8" />;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  let t2;
  if ($[12] !== date || $[13] !== futurePricingAvailability) {
    t2 = futurePricingAvailability?.find(futureDate => futureDate.date === formatDashedYYYYMMDD(date))?.priceTier || null;
    $[12] = date;
    $[13] = futurePricingAvailability;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const priceTier = t2;
  const isWithinSelectedDateRange = isSelectedStartOrEnd || !isSelectedStartOrEnd && range && (isWithinHoverRange || isSelected);
  const showLowPrice = priceTier === "low" && !isWithinSelectedDateRange && !disabledDate;
  const showMidPrice = priceTier === "mid" && !isWithinSelectedDateRange && !disabledDate;
  const showHighPrice = priceTier === "high" && !isWithinSelectedDateRange && !disabledDate;
  let t3;
  if ($[15] !== date || $[16] !== endDate || $[17] !== isSelected || $[18] !== isSelectedStartOrEnd || $[19] !== isWithinHoverRange || $[20] !== range || $[21] !== startDate) {
    t3 = clsx("flex flex-col items-center justify-center w-8 h-8 rounded-full relative focus-within:outline-dashed focus-within:outline-offset-2 focus-within:outline-status-focus mx-1", isSelectedStartOrEnd && "bg-black text-white", range && isSelectedStartOrEnd && startDate !== endDate && "before:absolute before:h-full before:w-2/3 before:-z-10 before:bg-neutral-200", !isSelectedStartOrEnd && range && (isWithinHoverRange || isSelected) && "before:absolute before:h-full before:w-full before:-z-10 before:bg-neutral-200 before:border-x-[0.25rem] before:border-neutral-200 before:box-content", isSameDay(startDate, date) && "before:-right-1", isSameDay(endDate, date) && "before:-left-1");
    $[15] = date;
    $[16] = endDate;
    $[17] = isSelected;
    $[18] = isSelectedStartOrEnd;
    $[19] = isWithinHoverRange;
    $[20] = range;
    $[21] = startDate;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== date || $[24] !== priceTier) {
    t4 = priceTier ? `${fullDate(date)}, price tier ${priceTier}` : fullDate(date);
    $[23] = date;
    $[24] = priceTier;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  const t5 = disabledDate && "cursor-not-allowed text-neutral-400 line-through";
  let t6;
  if ($[26] !== t5) {
    t6 = clsx("typography-caption-1 text-center pt-2 outline-none", t5);
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== day || $[29] !== disabledDate || $[30] !== onClick || $[31] !== onKeyDown || $[32] !== onMouseEnter || $[33] !== t4 || $[34] !== t6 || $[35] !== tabIndex) {
    t7 = <button onClick={onClick} onKeyDown={onKeyDown} onMouseEnter={onMouseEnter} tabIndex={tabIndex} type="button" disabled={disabledDate} aria-label={t4} className={t6}>{day}</button>;
    $[28] = day;
    $[29] = disabledDate;
    $[30] = onClick;
    $[31] = onKeyDown;
    $[32] = onMouseEnter;
    $[33] = t4;
    $[34] = t6;
    $[35] = tabIndex;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== showLowPrice) {
    t8 = showLowPrice && <span className="rounded-full bg-status-low w-1.5 h-1.5 inline-block border border-neutral-600" />;
    $[37] = showLowPrice;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== showMidPrice) {
    t9 = showMidPrice && <><span className="rounded-full bg-status-mid w-1.5 h-1.5 inline-block border border-neutral-600" /><span className="rounded-full bg-status-mid w-1.5 h-1.5 inline-block border border-neutral-600" /></>;
    $[39] = showMidPrice;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== showHighPrice) {
    t10 = showHighPrice && <><span className="rounded-full bg-status-high w-1.5 h-1.5 inline-block border border-neutral-600 " /><span className="rounded-full bg-status-high w-1.5 h-1.5 inline-block border border-neutral-600" /><span className="rounded-full bg-status-high w-1.5 h-1.5 inline-block border border-neutral-600" /></>;
    $[41] = showHighPrice;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t10 || $[44] !== t8 || $[45] !== t9) {
    t11 = <div className="space-x-0.5 relative h-1.5 text-center leading-[0]">{t8}{t9}{t10}</div>;
    $[43] = t10;
    $[44] = t8;
    $[45] = t9;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t11 || $[48] !== t3 || $[49] !== t7) {
    t12 = <div className={t3}>{t7}{t11}</div>;
    $[47] = t11;
    $[48] = t3;
    $[49] = t7;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  return t12;
}
export default Day;